import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowRight,
    GraduationCap,
    CheckCircle2,
    Shield,
    Sparkles,
    Globe,
    ChevronDown,
    Users2
} from 'lucide-react';

const ProductsSection = () => {
    const [expandedProduct, setExpandedProduct] = useState(null);

    const products = [
        {
            id: 'qivra-campus',
            name: 'QivraCampus AI',
            tagline: 'Complete School Management Excellence. AI-Powered. Future-Ready.',
            description: 'Schools are spending enormous amounts on outdated systems that still require manual attendance, paper report cards, and endless Excel sheets. QivraCampus AI doesn\'t just replace your legacy software — it obliterates it. AI-powered automation, zero paperwork, and measurable ROI from day one. Built for schools that demand excellence, not excuses.',
            icon: <GraduationCap className="w-16 h-16" />,
            gradient: 'from-cyan-400 via-blue-500 to-purple-600',
            glowColor: 'cyan',
            thumbnail: '/images/products/qivracampus-hero.png',
            features: [
                {
                    category: '💰 Massive Cost Savings & ROI',
                    items: [
                        'Massive Reduction in Admin Time - Fire the paperwork, not the people.',
                        'Significant Annual Savings - Eliminate manual processes, paper costs, and redundant software licenses.',
                        'ROI in First Month - Not first year. First MONTH. Measurable productivity gains from day one.',
                        'Lower Total Cost of Ownership - One system replaces multiple legacy tools. One price. Zero headaches.'
                    ]
                },
                {
                    category: '🤖 AI-Powered Intelligence',
                    items: [
                        'Camera-Based Attendance - AI vision technology with high accuracy. Real-time classroom monitoring.',
                        'Predictive Student Analytics - Identify at-risk students early with ML-powered insights.',
                        'Natural Language Bot - Simple text commands like "Mark John absent" execute instantly. Zero training needed.',
                        'Smart Report Cards - Auto-generated with personalized insights, trends, and actionable recommendations.'
                    ]
                },
                {
                    category: '⚡ Enterprise-Grade Performance',
                    items: [
                        'Lightning-Fast Queries - Load any report instantly, even for large student populations.',
                        'Real-Time Everything - Attendance, grades, fees update live across all devices. No delays.',
                        'Zero-Downtime Migration - Seamless transition from your existing system without disrupting operations.',
                        'Mobile-First Design - Full functionality on smartphones and tablets. Manage your school from anywhere.'
                    ]
                },
                {
                    category: '🎯 Advanced Automation Features',
                    items: [
                        'RFID Library System - Instant book check-in/out. Complete digital tracking and analytics.',
                        'Multi-Channel Fee Reminders - Automated WhatsApp, Email, SMS notifications for faster payment collection.',
                        'Digital Report Cards - Parents download PDFs instantly. Eliminate printing costs and distribution hassles.',
                        'Smart Inventory Alerts - Proactive notifications when supplies run low. Never run out of essentials again.'
                    ]
                },
                {
                    category: '🏆 Built for Modern Education',
                    items: [
                        'Modern Architecture - Cloud-native, scalable, and future-proof from the ground up.',
                        'Unlimited Users Included - No per-teacher pricing. Scale to any size at the same price.',
                        'Free Training & Migration - Complete onboarding, data migration, and staff training included. No hidden costs.',
                        'Indian School Compliance - CBSE, ICSE, State Boards fully supported. All regulations built-in from day one.'
                    ]
                },
                {
                    category: '🔒 Enterprise Security & Compliance',
                    items: [
                        'Bank-Grade Encryption - Protecting sensitive student and financial data at rest and in transit.',
                        'GDPR & Data Privacy Compliant - Full data protection compliance with parent data access and deletion rights.',
                        'Granular Role-Based Access - Customizable permissions ensuring users see only what they need to see.',
                        'Complete Audit Trails - Every action logged with timestamps and user details for full accountability.'
                    ]
                }
            ],
            techStack: {
                frontend: ['React 18', 'Vite', 'Tailwind CSS', 'Lucide-React'],
                backend: ['FastAPI', 'Hexagonal Architecture', 'JWT Auth', 'PBKDF2'],
                database: ['AWS RDS MySQL', 'SQLAlchemy 2.0', 'Pydantic V2']
            },
            performance: [],
            link: '#qivra-campus',
            demoLink: '#',
            status: 'Live & Deployed',
            statusType: 'success'
        },
        // Plant Ticket & Inventory Management System
        {
            id: 'plant-ticket-system',
            name: 'Plant Ticket & Inventory Management System',
            tagline: 'Zero Downtime. Maximum Accountability. Industrial-Grade Excellence.',
            description: 'Say goodbye to lost emails, forgotten maintenance requests, and finger-pointing. Our enterprise ticketing platform delivers what legacy systems can\'t: complete visibility, automated accountability, and measurable ROI from day one. Built for manufacturing plants that refuse to accept "good enough."',
            icon: <Shield className="w-16 h-16" />,
            gradient: 'from-purple-400 via-pink-500 to-red-500',
            glowColor: 'purple',
            thumbnail: '/images/products/plant-ticket-hero.png',
            features: [
                {
                    category: '💰 Immediate ROI & Cost Savings',
                    items: [
                        '75% Reduction in Mean Time to Resolution - Fix issues 4x faster than email-based systems',
                        '90% Decrease in Lost Tickets - No more "I never got that email" excuses',
                        '$50K+ Annual Savings - Eliminate wasted manhours from miscommunication',
                        'Zero Training Required - Operators learn in under 5 minutes with mobile-first design'
                    ]
                },
                {
                    category: '🎯 Unique Differentiators',
                    items: [
                        'QR Code Asset Tracking - Scan equipment to auto-populate tickets with complete asset history and specs.',
                        'Verified Closure System - Only the original reporter can close tickets, ensuring genuine issue resolution.',
                        'Email-to-Ticket Integration - IMAP automation converts support emails into trackable tickets automatically.',
                        'Midnight Cyber UI - Stunning dark-mode interface that operators love. Increases adoption and engagement.'
                    ]
                },
                {
                    category: '⚡ Speed & Intelligent Automation',
                    items: [
                        'Critical Issue Escalation - Auto-alerts to management within 15 minutes for high-priority incidents.',
                        'Unified Multi-Plant Dashboard - Manage unlimited facilities from one centralized command center.',
                        'Real-Time WebSocket Updates - Instant ticket updates across all devices. No manual refresh required.',
                        'Smart Assignment Engine - AI-powered routing to the right expert based on skills, workload, and history.'
                    ]
                },
                {
                    category: '📊 Executive Intelligence & Compliance',
                    items: [
                        'One-Click Professional Reports - Generate board-ready PDF and CSV exports with comprehensive analytics.',
                        'Complete Audit Trail - Every action logged with timestamps and user details. ISO/OSHA compliant.',
                        'Predictive Analytics - ML-powered insights identify potential issues before they become critical.',
                        'Performance Dashboards - Track team efficiency, resolution times, and SLA compliance in real-time.'
                    ]
                },
                {
                    category: '🏭 Multi-Site Excellence',
                    items: [
                        'Unlimited Facilities Included - No per-plant pricing. Scale across all your locations at one fixed cost.',
                        'Cross-Plant Knowledge Base - Share solutions and best practices instantly across all facilities.',
                        'Centralized Asset Registry - Complete visibility of all equipment, hardware, and assets across locations.',
                        'Regional Manager Dashboards - Hierarchical views perfect for executives overseeing multiple sites.'
                    ]
                },
                {
                    category: '🔒 Enterprise Security & Data Protection',
                    items: [
                        'JWT Token Authentication - Industry-standard secure authentication with encrypted token-based access.',
                        'SQL Injection Protection - TypeORM safeguards with parameterized queries and input validation.',
                        'Customizable Role-Based Permissions - Granular access control tailored to your organizational structure.',
                        'Complete Audit Logs - Full activity tracking with timestamps and user attribution for compliance.'
                    ]
                }
            ],
            techStack: {
                frontend: ['Next.js 14', 'React 18', 'Tailwind CSS', 'Framer Motion'],
                backend: ['NestJS', 'TypeORM', 'JWT Auth', 'IMAP Integration'],
                database: ['MySQL 8.0', 'Relational Mapping', 'Data Integrity']
            },
            performance: [],
            link: '#plant-ticket-system',
            demoLink: '#',
            status: 'Live & Deployed',
            statusType: 'success'
        },
        // QivraWall - Real-Time Collaborative Board
        {
            id: 'qivra-wall',
            name: 'QivraWall',
            tagline: 'Real-Time Collaborative Sticky-Note Board for Modern Teams.',
            description: 'Remote and hybrid teams struggle to collaborate on ideas. Chat threads lose context. Whiteboards aren\'t shareable. QivraWall changes everything — a lightweight, live infinite canvas where every participant can post ideas, organize thoughts, and vote on what resonates, simultaneously, from anywhere in the world. No downloads, no account needed, just share a link.',
            icon: <Users2 className="w-16 h-16" />,
            gradient: 'from-orange-400 via-amber-500 to-yellow-500',
            glowColor: 'orange',
            thumbnail: null,
            features: [
                {
                    category: '⚡ Instantly Ready',
                    items: [
                        'Create a password-protected room in 30 seconds.',
                        'Zero friction for participants — no downloads, no plugins, no account required.',
                        'Offline-resilient — works locally and shares boards via encoded URLs.',
                        'Instant room join with passkey verification. Guests get read-only access.'
                    ]
                },
                {
                    category: '🔴 Truly Live Collaboration',
                    items: [
                        'Real-time sync — every post, vote, and edit reaches all participants instantly.',
                        'Infinite canvas — unlimited space to expand ideas.',
                        'Color-coded cards — each participant\'s posts are visually distinct.',
                        'Live participant presence across the board.'
                    ]
                },
                {
                    category: '🗳️ Democratic Idea Voting',
                    items: [
                        'One like per person, per card — fair and abuse-proof.',
                        'Authors cannot vote on their own cards.',
                        'Vote counts update live for all participants.',
                        'The best ideas rise to the top naturally.'
                    ]
                },
                {
                    category: '🔐 Private & Secure by Design',
                    items: [
                        'Every room is passkey-protected.',
                        'Guest read-only mode — share results without exposing the editing layer.',
                        'No account or sign-up required for participants.',
                        'Room data scoped per session — no cross-room leakage.'
                    ]
                },
                {
                    category: '🎯 Perfect For',
                    items: [
                        'Brainstorming sessions',
                        'Sprint retrospectives',
                        'Product ideation & team kickoffs',
                        'Workshop facilitation'
                    ]
                }
            ],
            techStack: {
                frontend: ['React', 'Framer Motion', 'CSS3'],
                backend: ['Supabase', 'WebSockets', 'Real-time Subscriptions'],
                database: ['PostgreSQL', 'LocalStorage Fallback', 'URL Encoding']
            },
            performance: [],
            link: '#qivra-wall',
            demoLink: '#',
            status: 'Live & Available',
            statusType: 'success'
        },
        // Coming Soon - Future Innovation
        {
            id: 'coming-soon',
            name: 'More Innovation Coming Soon',
            tagline: 'The Future of Enterprise Solutions',
            description: 'We\'re constantly pushing boundaries and developing cutting-edge solutions to transform industries. Our next revolutionary product is in the works. Stay tuned for groundbreaking technology that will redefine what\'s possible.',
            icon: <Sparkles className="w-16 h-16" />,
            gradient: 'from-green-400 via-teal-500 to-blue-600',
            glowColor: 'green',
            thumbnail: null,
            features: [],
            techStack: {},
            performance: [],
            link: '#',
            demoLink: '#',
            status: 'Coming Soon',
            statusType: 'upcoming'
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    return (
        <section className="py-32 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
            {/* Background Effects */}
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(34, 211, 238, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(14, 165, 233, 0.1) 0%, transparent 50%)
          `
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-20"
                >
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                        <span className="text-cyan-400 text-sm font-light tracking-wide">Our Products</span>
                    </div>

                    <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
                        <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                            Enterprise Solutions
                        </span>
                    </h2>
                    <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
                        Production-ready products built with cutting-edge technology to transform your business operations
                    </p>
                </motion.div>

                {/* Products Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid lg:grid-cols-2 gap-12 items-start"
                >
                    {products.map((product, idx) => (
                        <motion.div
                            key={product.id}
                            variants={itemVariants}
                            className="group relative bg-gradient-to-b from-gray-900/80 to-black/90 backdrop-blur-sm rounded-3xl border border-gray-800/60 hover:border-cyan-400/40 transition-all duration-500 overflow-hidden flex flex-col"
                        >
                            {/* Glow Effect */}
                            <div className={`absolute inset-0 bg-gradient-to-br ${product.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                            <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="relative z-10 p-10 flex flex-col flex-grow">
                                {/* Product Header */}
                                <div className="flex items-start justify-between mb-8">
                                    <div className={`text-${product.glowColor}-400 group-hover:scale-110 transition-transform duration-300`}>
                                        {product.icon}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-4 py-1.5 rounded-full text-xs font-light border ${product.statusType === 'success'
                                            ? 'bg-green-500/10 text-green-400 border-green-400/30'
                                            : 'bg-purple-500/10 text-purple-400 border-purple-400/30'
                                            }`}>
                                            {product.status}
                                        </span>
                                    </div>
                                </div>

                                {/* Product Title */}
                                <h3 className={`text-3xl font-light mb-3 bg-gradient-to-r ${product.gradient} bg-clip-text text-transparent tracking-wide`}>
                                    {product.name}
                                </h3>
                                <p className="text-lg text-gray-400 mb-4 font-light tracking-wide">
                                    {product.tagline}
                                </p>
                                <p className="text-gray-300 mb-8 leading-relaxed font-light">
                                    {product.description}
                                </p>

                                {/* Product Image Showcase - Only for products with thumbnail */}
                                {product.thumbnail && (
                                    <div className="mb-8 rounded-2xl overflow-hidden border border-gray-800/60 hover:border-cyan-400/40 transition-all duration-500">
                                        <img
                                            src={product.thumbnail}
                                            alt={product.name}
                                            className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}

                                {/* Features Toggle - Only for products with features */}
                                {product.features.length > 0 && (
                                    <div className="mb-6">
                                        <button
                                            onClick={() => setExpandedProduct(expandedProduct === idx ? null : idx)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-light tracking-wide transition-all duration-300 border ${expandedProduct === idx
                                                ? 'text-cyan-300 bg-cyan-500/10 border-cyan-400/40'
                                                : 'text-gray-400 bg-gray-800/40 border-gray-700/40 hover:text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-400/30'
                                                }`}
                                        >
                                            <Sparkles className="w-4 h-4" />
                                            {expandedProduct === idx ? 'Hide Features' : 'View All Features'}
                                            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expandedProduct === idx ? 'rotate-180' : ''}`} />
                                        </button>
                                    </div>
                                )}

                                {/* Features Expandable - Only when toggled for THIS card */}
                                <AnimatePresence>
                                    {product.features.length > 0 && expandedProduct === idx && (
                                        <motion.div
                                            key={`features-${product.id}`}
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.4, ease: "easeInOut" }}
                                            className="overflow-hidden mb-8"
                                        >
                                            <div className="space-y-6 pt-2">
                                                {product.features.map((featureGroup, fIdx) => (
                                                    <div key={fIdx}>
                                                        <h4 className="text-cyan-300 font-medium mb-3 text-sm tracking-wide">
                                                            {featureGroup.category}
                                                        </h4>
                                                        <ul className="space-y-2">
                                                            {featureGroup.items.map((item, iIdx) => (
                                                                <motion.li
                                                                    key={iIdx}
                                                                    initial={{ opacity: 0, x: -20 }}
                                                                    animate={{ opacity: 1, x: 0 }}
                                                                    transition={{ delay: iIdx * 0.03 }}
                                                                    className="flex items-start gap-2 text-gray-400 text-sm"
                                                                >
                                                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                                                    <span className="font-light">{item}</span>
                                                                </motion.li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 mt-auto pt-8">
                                    {product.statusType === 'success' ? (
                                        <>
                                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} className="flex-1">
                                                <Link
                                                    to="/contact?product=true"
                                                    className={`w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r ${product.gradient} px-6 py-3 rounded-xl font-light tracking-wide text-white hover:shadow-2xl hover:shadow-${product.glowColor}-400/40 transition-all duration-300 border border-${product.glowColor}-400/40 group/btn cursor-pointer`}
                                                >
                                                    <span>Learn More</span>
                                                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                                                </Link>
                                            </motion.div>
                                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} className="flex-1">
                                                <Link
                                                    to="/contact?product=true"
                                                    className={`w-full inline-flex items-center justify-center gap-2 border-2 border-${product.glowColor}-400/60 text-${product.glowColor}-400 px-6 py-3 rounded-xl font-light tracking-wide hover:bg-${product.glowColor}-400/10 hover:border-${product.glowColor}-400 transition-all duration-300`}
                                                >
                                                    <span>View Demo</span>
                                                    <Globe className="w-4 h-4" />
                                                </Link>
                                            </motion.div>
                                        </>
                                    ) : (
                                        <motion.div whileHover={{ scale: 1.02 }} className="w-full">
                                            <div className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-3 rounded-xl font-light tracking-wide text-gray-400 border border-gray-700/40 cursor-not-allowed">
                                                <Sparkles className="w-4 h-4" />
                                                <span>Stay Tuned</span>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="text-center mt-20"
                >
                    <p className="text-gray-400 mb-6 font-light tracking-wide">
                        Interested in custom enterprise solutions?
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-400 to-purple-600 px-10 py-5 rounded-2xl font-light tracking-wide text-white hover:shadow-2xl hover:shadow-cyan-400/40 transition-all hover:scale-105 duration-300 border border-cyan-400/40 group"
                    >
                        <span>Talk to Our Team</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default ProductsSection;
